import React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { useSelector } from 'react-redux';
import {
  ArticleContent,
  ConditionalArticleMainImage,
  ArticleTitle,
  SocialButtons,
  RelatedPosts,
  Slideshow,
  RecommendationsAdvertisement,
  MEDIA_BREAKPOINTS,
  PostDates,
  ContentAndSidebar,
  PostMainContent,
} from 'mm-ui-components';
import { createBlocksWidth } from '../postPage.util';
import {
  ArticleComponentDataProps,
  FiniteScrollArticleComponentDataProps,
  MainArticleComponentDataProps,
} from '../utils/articleComponent.utils';
import { DisclaimersAtDynamicPosition } from '../../components/disclaimer/DisclaimersAtDynamicPosition';
import { getUseFormatMinutesHoursAgo } from '../../store/config/config.selectors';
import { HeaderPostSidebarConnected } from '../../components/connectedComponents/PostSidebarConnected';
import { AdditionalStyleSheet } from '../templates.utils';

const defaultBlocksWidths = createBlocksWidth(740, 720, 640);

const createStyles = (isFullBleed: boolean) => StyleSheet.create({
  socialContainer: {
    [MEDIA_BREAKPOINTS.large]: {
      margin: isFullBleed ? '30px 20px' : '20px 20px 30px',
    },
    [MEDIA_BREAKPOINTS.medium]: {
      margin: '20px 0 30px',
    },
    [MEDIA_BREAKPOINTS.small]: {
      margin: '20px 20px 30px',
    },
  },
  divider: {
    [MEDIA_BREAKPOINTS.small]: {
      margin: '0 20px',
    },
  },
  datesContainer: {
    [MEDIA_BREAKPOINTS.large]: {
      paddingLeft: '20px',
    },
    [MEDIA_BREAKPOINTS.medium]: {
      maxWidth: '720px',
      padding: '0px 20px',
    },
    [MEDIA_BREAKPOINTS.small]: {
      maxWidth: '640px',
      margin: '20px auto 0',
      padding: '0px 20px',
    },
  },
});

const contentAdditionalStyles = () => StyleSheet.create({
  style: {
    [MEDIA_BREAKPOINTS.large]: {
      padding: '0',
    },
  },
}) as AdditionalStyleSheet;

const sidebarAdditionalStyles = () => StyleSheet.create({
  style: {
    [MEDIA_BREAKPOINTS.large]: {
      gridTemplateColumns: '2fr 340px',
    },
  },
}) as AdditionalStyleSheet;

export const FijiArticleComponent: React.FunctionComponent<
  ArticleComponentDataProps
> = props => {
  const {
    title,
    intro,
    by,
    createdAt,
    createdAtISO,
    updatedAt,
    updatedAtISO,
    showUpdatedAt,
    authors,
    shareConfig,
    cover,
    siteName,
    templateName,
    body,
    slideshow,
    commercialTags,
    ownerUsername,
    mainArticleURL,
    resourceID,
    sideBar2AdOnSuccess,
    sideBar1AdOnSuccess,
    sideBar1Id,
    sideBar2Id,
    disclaimerText,
    reviewDisclaimerText,
    shouldDisplayDisclaimerAtBottom,
    showRecommendationsAdvertisement,
    outbrainWidgetID,
    shouldShowHeroImage,
  } = props;
  const { experiments, canonical, addMMPlayerPlaceholder = false } = props as MainArticleComponentDataProps;
  const { articleUrl } = props as FiniteScrollArticleComponentDataProps;
  const { relatedPosts, isFullBleed = false } = props as any;
  const url = mainArticleURL || canonical || articleUrl;
  const useFormatMinutesHoursAgo = useSelector(getUseFormatMinutesHoursAgo);
  const styles = createStyles(isFullBleed);

  return (
    <div>
      <ContentAndSidebar additionalStyles={sidebarAdditionalStyles()}>
        <PostMainContent additionalStyles={contentAdditionalStyles()}>
          <header>
            <ArticleTitle
              title={title}
              by={by}
              createdAt={createdAt}
              createdAtISO={createdAtISO}
              updatedAt={updatedAt}
              updatedAtISO={updatedAtISO}
              showUpdatedAt={showUpdatedAt}
              authors={authors}
              intro={intro}
              useFormatMinutesHoursAgo={useFormatMinutesHoursAgo}
            />
            {!isFullBleed && shouldShowHeroImage && (
              <ConditionalArticleMainImage
                {...cover}
                siteName={siteName}
                templateName={templateName}
                defaultBlocksWidths={defaultBlocksWidths}
                paddingOverride={{
                  largePadding: 0,
                  mediumPadding: 0,
                  smallPadding: 0,
                }}
              />
            )}
            <div className={css(styles.socialContainer)}>
              <SocialButtons shareConfig={shareConfig} canonical={canonical} />
            </div>
            <hr className={css(styles.divider)} />
          </header>
          <Slideshow
            {...slideshow}
            experiments={experiments}
            resourceID={resourceID}
            commercialTags={commercialTags}
            ownerUsername={ownerUsername}
            blockWidths={defaultBlocksWidths}
          >
            <DisclaimersAtDynamicPosition
              disclaimerText={disclaimerText}
              reviewDisclaimerText={reviewDisclaimerText}
              defaultBlocksWidths={defaultBlocksWidths}
              shouldDisplayDisclaimerAtBottom={
                shouldDisplayDisclaimerAtBottom
              }
            >
              <ArticleContent
                body={body}
                templateName={templateName}
                siteName={siteName}
                defaultBlocksWidths={defaultBlocksWidths}
                blocksPaddingOverride={{ smallPadding: 0, mediumPadding: 0, largePadding: 0 }}
                totalSlides={slideshow.totalSlides}
                experiments={experiments}
                commercialTags={commercialTags}
                slidesLinks={slideshow.slidesLinks}
                resourceID={resourceID}
                ownerUsername={ownerUsername}
                addMMPlayerPlaceholder={addMMPlayerPlaceholder}
                imageWidthLarge={620}
                imageWidthMedium={620}
                imageWidthSmall={360}
                mmVideoPlaceholderId="mmvid"
              />
              <div className={css(styles.datesContainer)}><PostDates {...{ updatedAt, updatedAtISO, createdAt, createdAtISO, showUpdatedAt }} /></div>
            </DisclaimersAtDynamicPosition>
          </Slideshow>
        </PostMainContent>
        <HeaderPostSidebarConnected
          adId1={sideBar1Id}
          adId2={sideBar2Id}
          adOnSuccess2={sideBar2AdOnSuccess}
          adOnSuccess1={sideBar1AdOnSuccess}
          postAsideChildren={relatedPosts
            ? <RelatedPosts {...{ ...relatedPosts, defaultBlocksWidths }} /> : undefined}
        />
        {showRecommendationsAdvertisement && !!outbrainWidgetID
            ? <RecommendationsAdvertisement experiment={experiments?.join()} outbrainWidgetID={outbrainWidgetID} pageLiveUrl={url} /> : null}
      </ContentAndSidebar>
    </div>
  );
};
